import {
	AfterViewInit,
	Component,
	EventEmitter,
	Input,
	Output,
	ViewChild,
	ElementRef,
	OnDestroy,
} from '@angular/core'
import * as intlTelInput from 'intl-tel-input' // Import default instead of namespace

@Component({
	selector: 'app-intl-tel-input',
	templateUrl: './intl-tel-input.component.html',
	styleUrls: ['./intl-tel-input.component.scss'],
})
export class IntlTelInputComponent implements AfterViewInit, OnDestroy {
	@ViewChild('telInput') input!: ElementRef<HTMLInputElement> // Add type for ViewChild
	@Input() phoneNumber = ''
	@Input() cssClass = 'form-control'
	@Input() hasError = false;

	@Output() phoneNumberChange = new EventEmitter<string>()
	@Output() dispatchOnBlur = new EventEmitter<void>()
	@Output() dispatchOnKeyUp = new EventEmitter<void>()

	private sinchCountriesIsoCodes = [
		"ad", "ae", "af", "ag", "ai", "al", "am", "an", "ao", "aq", 
		"ar", "as", "at", "au", "aw", "ax", "az", "ba", "bb", "bd", 
		"be", "bf", "bg", "bh", "bi", "bj", "bl", "bm", "bn", "bo", 
		"bq", "br", "bs", "bt", "bv", "bw", "by", "bz", "ca", "cc", 
		"cd", "cf", "cg", "ch", "ci", "ck", "cl", "cm", "cn", "co", 
		"cr", "cu", "cv", "cw", "cx", "cy", "cz", "de", "dj", "dk", 
		"dm", "do", "dz", "ec", "ee", "eg", "eh", "er", "es", "et", 
		"fi", "fj", "fk", "fm", "fo", "fr", "ga", "gb", "gd", "ge", 
		"gf", "gg", "gh", "gi", "gl", "gm", "gn", "gp", "gq", "gr", 
		"gs", "gt", "gu", "gw", "gy", "hk", "hm", "hn", "hr", "ht", 
		"hu", "id", "ie", "il", "im", "in", "io", "iq", "ir", "is", 
		"it", "je", "jm", "jo", "jp", "ke", "kg", "kh", "ki", "km", 
		"kn", "kp", "kr", "kw", "ky", "kz", "la", "lb", "lc", "li", 
		"lk", "lr", "ls", "lt", "lu", "lv", "ly", "ma", "mc", "md", 
		"me", "mf", "mg", "mh", "mk", "ml", "mm", "mn", "mo", "mp", 
		"mq", "mr", "ms", "mt", "mu", "mv", "mw", "mx", "my", "mz", 
		"na", "nc", "ne", "nf", "ng", "ni", "nl", "no", "np", "nr", 
		"nu", "nz", "om", "pa", "pe", "pf", "pg", "ph", "pk", "pl", 
		"pm", "pn", "pr", "ps", "pt", "pw", "py", "qa", "re", "ro", 
		"rs", "ru", "rw", "sa", "sb", "sc", "sd", "se", "sg", "sh", 
		"si", "sj", "sk", "sl", "sm", "sn", "so", "sr", "ss", "st", 
		"sv", "sx", "sy", "sz", "tc", "td", "tf", "tg", "th", "tj", 
		"tk", "tl", "tm", "tn", "to", "tr", "tt", "tv", "tw", "tz", 
		"ua", "ug", "um", "us", "uy", "uz", "va", "vc", "ve", "vg", 
		"vi", "vn", "vu", "wf", "ws", "ye", "yt", "za", "zm", "zw"
	];

	private iti: any // Add type for intlTelInput instance
	private initialCountry: keyof typeof this.masks = 'br'
	private preferredCountries = ['br', 'us', 'de', 'es', 'fr', 'it']
	private masks = {
		br: '(00) 00000-0000',
		us: '(000) 000-0000',
		de: '0000 0000000',
		es: '000 00 00 00',
		fr: '0 00 00 00 00',
		it: '000 0000000',
	}
	
	selectedCountry: keyof typeof this.masks = this.initialCountry
	mask = this.masks[this.selectedCountry]
	selectedCountryCode = ''

	constructor() {
		window.addEventListener('countrychange', () => this.onPhoneNumberChange())
	}

	ngAfterViewInit() {
		this.initializeIntlTelInput()
	}

	ngOnDestroy() {
		window.removeEventListener('countrychange', () => this.onPhoneNumberChange())
		this.iti.destroy()
	}

	initializeIntlTelInput() {
		this.iti = intlTelInput(this.input.nativeElement, {
			// utilsScript: 'assets/scripts/utils.js',
			nationalMode: true,
			separateDialCode: true,
			formatOnDisplay: true,
			initialCountry: this.initialCountry,
			preferredCountries: this.preferredCountries,
			onlyCountries: this.sinchCountriesIsoCodes,
		})
		this.selectedCountryCode = this.iti.getSelectedCountryData().dialCode
	}

	onFocus() {
		if (!this.phoneNumber) {
			this.selectedCountryCode = this.iti.getSelectedCountryData().dialCode
			return
		}
		if (!this.hasError) {
			this.onPhoneNumberChange()
		}
	}

	onBlur() {
		if (!this.hasError) {
			this.onPhoneNumberChange()
		}
		this.dispatchOnBlur.emit();
	}

	onKeyUp() {
		this.dispatchOnKeyUp.emit();
	}

	onInputKeyPress(event: KeyboardEvent) {
		const allowedChars = /[0-9\+\-\ ]/
		const allowedCtrlChars = /[axcv]/ // Allows copy-pasting
		const allowedOtherKeys = [
			'ArrowLeft',
			'ArrowUp',
			'ArrowRight',
			'ArrowDown',
			'Home',
			'End',
			'Insert',
			'Delete',
			'Backspace',
		]

		if (
			!allowedChars.test(event.key) &&
			!(event.ctrlKey && allowedCtrlChars.test(event.key)) &&
			!allowedOtherKeys.includes(event.key)
		) {
			event.preventDefault()
			return
		}

		this.onPhoneNumberChange()
	}

	onPhoneNumberChange() {
		const { iso2, dialCode } = this.iti.getSelectedCountryData()
		this.selectedCountry = iso2
		this.selectedCountryCode = dialCode
		this.mask = this.masks[this.selectedCountry]

		this.phoneNumberChange.emit(`${this.selectedCountryCode}-${this.phoneNumber}`)
	}
}
